import { MouseEventHandler, ReactNode, useEffect, useMemo, useState } from "react";
import "./App.css";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import socketIOClient from "socket.io-client";
import TopBar, { TopBarAsButton } from "./components/TopBar";
import About from "./components/pages/about";
import { UserPage } from "./components/pages/UserPage";
import { FeedbackResult } from "./components/pages/feedbackResult";
import { ProjectAdmin } from "./components/pages/projectAdmin/projectAdmin";
import { Socket } from "socket.io-client";
import { SocketEvents, UserPermission } from "./components/types";
import ComponentEditorTab from "./components/ComponentEditor/ComponentEditor";
import TableContainer from "./components/gametable/redesign/TableContainer";
import { PasswordGate } from "./PasswordGate";
import Home from "./components/pages/Home";
import FullTopBarLayout from "./components/FullTopBarLayout";
import ProjectDetail from "src/components/ProjectDetail";
import { Helmet as HelmetImport, HelmetProps } from "react-helmet";
import FontAdmin, { FontDefinition } from "src/components/pages/FontAdmin";
import IngameFeedbackResults from "src/components/pages/IngameFeedbackResults";
import { makeUserContext, UserContext } from "src/components/userContext";
const Helmet = HelmetImport as unknown as (props: HelmetProps) => ReactNode; //https://stackoverflow.com/a/79340559/4030631

const App = () => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [username, setUsername] = useState<string | null>(null);
  const navigate = useNavigate();

  function logout() {
    fetch("/api/logout", {
      method: "POST",
    })
      .then(() => {
        localStorage.removeItem("user-name");
        localStorage.removeItem("user-role");
        setLoggedIn(false);
        setUsername(null);
        navigate("/");
      })
      .catch((error) => {
        console.log(error);
      });
  }

  function pingLogin() {
    fetch("/api/pingLogin", {
      method: "GET",
    })
      .then((res) => {
        if (res.status === 200) {
          setLoggedIn(true);
        }
        return res.json();
      })
      .then((data) => {
        localStorage.setItem("user-name", data.username);
        setUsername(data.username);

        if (data.serverEnv) {
          localStorage.setItem("server-env", data.serverEnv);
        }

        if (data.dbEnv) {
          localStorage.setItem("db-env", data.dbEnv);
        }
      });
  }

  useEffect(() => {
    pingLogin();
  }, []);

  let skipPasswordGate = loggedIn || window.location.href.includes("invitePwd");

  const topBarButtonEl = <TopBarAsButton />;

  return (
    <div className="app">
      {skipPasswordGate ? (
        <Main logout={logout} loggedIn={loggedIn} topBarButtonEl={topBarButtonEl} username={username} />
      ) : (
        <>
          <TopBar logout={logout} loggedIn={loggedIn} />
          <PasswordGate setLoggedIn={setLoggedIn} />
        </>
      )}
    </div>
  );
};

function Main(props: {
  logout: MouseEventHandler;
  loggedIn: boolean;
  topBarButtonEl: ReactNode;
  username: string | null;
}) {
  const [socketState, setSocketState] = useState<Socket<SocketEvents> | undefined>();
  const [connected, setConnected] = useState<boolean>(false);
  const [socketAmt, setSocketAmt] = useState<number>(0);
  const [initialConnect, setInitialConnect] = useState(false);
  const [fontDefinitions, setFontDefinitions] = useState<Array<FontDefinition>>([]);
  const [reconnectAttempt, setReconnectAttempt] = useState<number>(0);
  const [lastPong, setLastPong] = useState<number>(Date.now());
  const [userPermissions, setUserPermissions] = useState<Array<UserPermission>>([]);

  useEffect(() => {
    console.log("setting up socketio client");
    setSocketState(socketIOClient());
    loadFontDefinitions();
  }, []);

  function loadFontDefinitions() {
    fetch("/api/fonts", {
      method: "GET",
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.fonts) {
          setFontDefinitions(data.fonts);
        }
      })
      .catch((error) => {
        console.error("Error fetching font definitions:", error);
      });
  }

  useEffect(() => {
    if (socketState) {
      //window.socket = socketState
      console.log("connecting socketio");
      const handleInstanceId = (id: string) => {
        const lastId = localStorage.getItem("lastInstanceId");

        if (lastId && lastId !== id) {
          localStorage.removeItem("lastInstanceId");
          console.warn("New server instance detected, reloading...");
          window.location.reload();
        } else {
          localStorage.setItem("lastInstanceId", id);
        }
      };
      const handleConnect = () => {
        console.log(socketState);
        setConnected(true);
        setReconnectAttempt(0);
        setLastPong(Date.now()); // Reset heartbeat timer on connect
        setSocketAmt((a) => a + 1);
        socketState.emit("setUsername", localStorage.getItem("user-name"));

        // Only create a new sessionId on the first connect, not on reconnects
        // This ensures room memberships are preserved after reconnection
        let existingSessionId = sessionStorage.getItem("sessionId");
        if (!existingSessionId) {
          let sessionNum = "" + (1 + +(localStorage.getItem("sessionNum") ?? 0));
          localStorage.setItem("sessionNum", sessionNum);
          existingSessionId = localStorage.getItem("user-name") + sessionNum;
          sessionStorage.setItem("sessionId", existingSessionId);
        }
        socketState.emit("registerSession", existingSessionId);
        setInitialConnect((prev) => {
          return true;
        });
      };

      const handleDisconnect = (reason: string) => {
        console.warn("Socket disconnected:", reason);
        setConnected(false);
      };

      const handleReconnectAttempt = (attempt: number) => {
        console.log("Reconnection attempt:", attempt);
        setReconnectAttempt(attempt);
      };

      const handleConnectError = (error: Error) => {
        console.error("Socket connection error:", error.message);
      };

      // Heartbeat: socket.io has built-in ping/pong, we track when we receive pong
      const handlePong = () => {
        setLastPong(Date.now());
      };

      socketState.on("connect", handleConnect);
      socketState.on("instanceId", handleInstanceId);
      socketState.on("disconnect", handleDisconnect);
      socketState.io.on("reconnect_attempt", handleReconnectAttempt);
      socketState.io.on("error", handleConnectError);
      socketState.io.on("ping", handlePong); // socket.io engine emits ping before sending

      return () => {
        socketState.off("instanceId", handleInstanceId);
        socketState.off("connect", handleConnect);
        socketState.off("disconnect", handleDisconnect);
        socketState.io.off("reconnect_attempt", handleReconnectAttempt);
        socketState.io.off("error", handleConnectError);
        socketState.io.off("ping", handlePong);
      };
    }
  }, [socketState]);

  // Detect zombie connections: if no ping/pong for too long, force reconnect
  useEffect(() => {
    if (!socketState || !connected) return;

    const HEARTBEAT_TIMEOUT = 35000; // Socket.io default pingInterval is 25s, timeout is 20s
    const checkInterval = setInterval(() => {
      const timeSinceLastPong = Date.now() - lastPong;
      if (timeSinceLastPong > HEARTBEAT_TIMEOUT) {
        console.warn(`No heartbeat for ${timeSinceLastPong}ms, connection may be dead. Forcing reconnect...`);
        setConnected(false);
        socketState.disconnect();
        socketState.connect();
      }
    }, 10000);

    return () => clearInterval(checkInterval);
  }, [socketState, connected, lastPong]);

  useEffect(() => {
    if (socketState) {
      (window as any).debugSocket = socketState;
      console.log("Debug: Use window.debugSocket.disconnect() to simulate disconnect");
    }
  }, [socketState]);

  useEffect(() => {
    if (!props.username) return;
    fetch(`/api/user/permissions`, {
      method: "GET",
    })
      .then((res) => {
        if (res.status === 200) {
          return res.json();
        } else {
          console.error("Failed to fetch user permissions:", res.status);
          return null;
        }
      })
      .then((data) => {
        if (data) {
          setUserPermissions(data);
        } else {
          console.log("No permissions data received");
        }
      })
      .catch((error) => {
        console.error("Error fetching user permissions:", error);
      });
  }, [props.username]);

  let projectEl = useMemo(
    () => (socketState ? <ProjectDetail socket={socketState} connected={connected} socketAmt={socketAmt} /> : <></>),
    [socketAmt],
  );

  let contextValue = useMemo(() => {
    return makeUserContext(props.username || "", userPermissions);
  }, [props.username, userPermissions]);

  if (!socketState?.on) {
    return <div className="project-main">Please wait, connecting websocket</div>;
  }

  if (!initialConnect) {
    return <div>Connecting...</div>;
  }

  const fullTopBarEl = (
    <TopBar logout={props.logout} loggedIn={props.loggedIn} connected={connected} reconnectAttempt={reconnectAttempt} />
  );

  return (
    <UserContext.Provider value={contextValue}>
      <Helmet>
        {fontDefinitions.map((font) => (
          <link key={font.id} rel="stylesheet" href={font.url} />
        ))}
      </Helmet>
      <Routes>
        <Route element={<FullTopBarLayout fullTopBarEl={fullTopBarEl} />}>
          <Route path="/" element={<Home />} />

          <Route path="/fontAdmin" element={<FontAdmin />} />
          <Route path="/projects/:projectSlug/admin/component-editor" element={<ComponentEditorTab />} />
          <Route path="/projects/:projectSlug" element={projectEl} />
          <Route path="/projects/:projectSlug/feedback" element={<FeedbackResult />} />
          <Route path="/projects/:projectSlug/admin" element={<ProjectAdmin />} />
          <Route path="/user/:username" element={<UserPage />} />
          <Route path="/projects/:projectSlug/ingame-feedback" element={<IngameFeedbackResults />} />
        </Route>

        <Route
          path="/projects/:projectSlug/table/:tableId"
          element={
            <TableContainer
              socket={socketState}
              connected={connected}
              reconnectAttempt={reconnectAttempt}
              topBarAsButton={props.topBarButtonEl}
            />
          }
        >
          <Route index element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </UserContext.Provider>
  );
}

export default App;
